// src/features/orders/ordersSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Define the interface for a single order status history item.
// This matches the data structure from your backend.
interface OrderStatusHistory {
    id: string;
    status: string;
    subStatus?: string;
    details: string;
    comment: string | null;
    createdAt: string;
    awbNumber: string;
    warehouseName: string;
    userName: string;
    userType: string;
    orderId: string; // 🎯 Crucial: Ensure this is always present in the payload
}

// Define the interface for the entire state of the 'orders' slice.
interface OrderState {
    list: any[]; // Or a more specific type if you have one
    statusHistory: Record<string, OrderStatusHistory[]>;
    loading: boolean;
    error: string | null;
}

interface Order {
    id: string;
    status: string;
    [key: string]: any; // Allows for other properties
}

// Define the initial state with a clear and predictable structure.
const initialState: OrderState = {
    list: [],
    statusHistory: {},
    loading: false,
    error: null,
};

// Create the slice using Redux Toolkit's createSlice function.
const ordersSlice = createSlice({
    name: 'orders',
    initialState,
    reducers: {
        // Reducer for when the initial order list is fetched from an API.
        setOrdersList(state, action: PayloadAction<any[]>) {
            state.list = action.payload;
            state.loading = false;
        },

        // Reducer to set the complete status history for a specific order.
        // This is used for the initial fetch on the OrderDetails page.
        setOrderHistory(state, action: PayloadAction<{ orderId: string, history: OrderStatusHistory[] }>) {
            state.statusHistory[action.payload.orderId] = action.payload.history;
        },

        // 🎯 Reducer to handle a single new real-time status update.
        addSingleStatusUpdate(state: any, action: PayloadAction<OrderStatusHistory>) {
            // 🎯 1. Check if the orderId is valid at the start.
            const { orderId } = action.payload;
            if (!orderId) {
                console.error("❌ AddStatusUpdate: orderId is missing from the payload", action.payload);
                return; // Exit the reducer early
            }

            // 🎯 2. Explicitly ensure the history for this orderId is a valid array.
            // We check for falsiness (undefined) OR if it's not an array.
            if (!state.statusHistory[orderId] || !Array.isArray(state.statusHistory[orderId])) {
                state.statusHistory[orderId] = [];
            }

            // Now, you can be certain that `state.statusHistory[orderId]` is an array.
            state.statusHistory[orderId].push(action.payload);

            // 🎯 3. Update the status of the order in the main list.
            //    We need to handle the case where `state.list` is now an object
            //    with a nested `orders` array.
            //    This assumes your `state.list` is an object, as we discussed previously.
            if (state.list && state.list.orders && Array.isArray(state.list.orders)) {
                const orderInList = state?.list?.orders?.find((o: any) => o.id === orderId);
                if (orderInList) {
                    orderInList.status = action.payload.status;
                }
            }
        },
        // ⭐ New Reducer to add a single new order to the list.
        addNewOrder(state: any, action: PayloadAction<Order>) {
            const newOrder = action.payload;
            // Check if the order already exists in the list to prevent duplicates
            const existingOrder = state?.list?.orders?.find((order: any) => order.id === newOrder.id);
            if (!existingOrder) {
                // Add the new order to the beginning of the list
                state?.list?.orders?.unshift(newOrder);

                // ⭐ If the list now exceeds 10 items, remove the last one
                if (state?.list?.length > 10) {
                    state?.list?.orders.pop();
                }
            }
        },
        // You can add other reducers here as needed, e.g., for loading and error states.
        setLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload;
        },
        setError(state, action: PayloadAction<string | null>) {
            state.error = action.payload;
        },
    },
});

// Export the actions from the slice for use in components and hooks.
export const {
    setOrdersList,
    setOrderHistory,
    addSingleStatusUpdate,
    addNewOrder,
    setLoading,
    setError,
} = ordersSlice.actions;

// Export the reducer to be combined in the store.
export default ordersSlice.reducer;